import { CHECKLIST_LIMITS, roleAtLeast, type TenantRole } from "@fts/core";
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { EmptyState, Note, PageHeader, Panel } from "../components/ui";
import type { ChecklistFault } from "../data/provider";
import { useI18n } from "../i18n";
import { formatAlarmTime, personName } from "../lib/alarms";
import { useAlertPeople } from "../state/alarms";
import { useSession, type TenantData } from "../state/session";
import { itemText } from "./ChecklistParts";

/** Faults are resolved by fleet managers and above on the fault's site (resolve_checklist_fault). */
export const canResolveFaults = (role: TenantRole | null): boolean => !!role && roleAtLeast(role, "fleet_manager");

type StatusFilter = "open" | "resolved" | "all";

/** Fault rows; `showVehicle` adds the vehicle column (faults list), the vehicle tab hides it. */
export function FaultsTable({ data, faults, showVehicle, onChanged }: { data: TenantData; faults: ChecklistFault[]; showVehicle: boolean; onChanged: () => void }) {
  const { t, lang, locale } = useI18n();
  const { role, user } = useSession();
  const { people } = useAlertPeople();
  const [resolving, setResolving] = useState<ChecklistFault | null>(null);
  const mayResolve = canResolveFaults(role);
  if (faults.length === 0) return <p className="table-empty">{t("faults.empty")}</p>;
  return (
    <>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              {showVehicle && <th scope="col">{t("faults.vehicle")}</th>}
              <th scope="col">{t("faults.item")}</th>
              <th scope="col" className="col-hide-sm">
                {t("faults.note")}
              </th>
              <th scope="col" className="col-hide-sm">
                {t("faults.reported")}
              </th>
              <th scope="col">{t("faults.status")}</th>
            </tr>
          </thead>
          <tbody>
            {faults.map((f) => {
              const v = data.vehicles.find((x) => x.id === f.vehicleId);
              return (
                <tr key={f.id}>
                  {showVehicle && (
                    <td>
                      <a className="subtle-link mono" href={`#/vehicles/${encodeURIComponent(f.vehicleId)}/checklist`}>
                        {v?.fleetNo ?? t("common.empty")}
                      </a>
                    </td>
                  )}
                  <td>
                    <span className="mono muted">{f.itemCode}</span> {itemText(lang, f)}
                    {f.critical && <span className="badge tone-crit cl-inline-badge">{t("checklist.critical")}</span>}
                  </td>
                  <td className="col-hide-sm">{f.note ?? t("common.empty")}</td>
                  <td className="col-hide-sm nowrap">{formatAlarmTime(f.createdAt, locale)}</td>
                  <td>
                    {f.status === "resolved" ? (
                      <>
                        <span className="badge tone-ok">{t("faults.status.resolved")}</span>
                        <div className="muted small">
                          {t("faults.resolvedBy", { name: personName(t, people, f.resolvedBy, user?.id), time: formatAlarmTime(f.resolvedAt, locale) ?? "" })}
                        </div>
                        {f.resolutionNote && <div className="small">{f.resolutionNote}</div>}
                      </>
                    ) : mayResolve ? (
                      <button type="button" className="button button-secondary button-sm" onClick={() => setResolving(f)}>
                        <Icon name="check" size={15} />
                        {t("faults.resolve")}
                      </button>
                    ) : (
                      <span className="badge tone-warn">{t("faults.status.open")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!mayResolve && faults.some((f) => f.status === "open") && <p className="panel-footnote">{t("faults.needsRole")}</p>}
      {resolving && (
        <ResolveDialog
          fault={resolving}
          onClose={() => setResolving(null)}
          onDone={() => {
            setResolving(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

function ResolveDialog({ fault, onClose, onDone }: { fault: ChecklistFault; onClose: () => void; onDone: () => void }) {
  const { t, lang } = useI18n();
  const { provider } = useSession();
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const tooLong = note.trim().length > CHECKLIST_LIMITS.resolutionNoteMax;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy || tooLong) return;
    setBusy(true);
    setError(null);
    try {
      await provider.resolveChecklistFault(fault.id, note.trim() || null);
      onDone();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t("faults.resolveTitle")}
      description={`${fault.itemCode} · ${itemText(lang, fault)}`}
      onClose={() => !busy && onClose()}
      onSubmit={(e) => void submit(e)}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || tooLong}>
            {busy ? t("common.working") : t("faults.resolve")}
          </button>
        </>
      }
    >
      <FormField id="fault-note" label={t("faults.resolveNote")} hint={t("faults.resolveHint")} optional error={tooLong ? t("cf.issue.note_too_long") : null}>
        <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} {...fieldAria("fault-note", { hint: true, error: tooLong ? "x" : null })} />
      </FormField>
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

export function ChecklistFaultsPage({ data }: { data: TenantData }) {
  const { t } = useI18n();
  const { provider, tenant } = useSession();
  const [filter, setFilter] = useState<StatusFilter>("open");
  const [faults, setFaults] = useState<ChecklistFault[] | null>(null);
  const [error, setError] = useState(false);
  const tenantId = tenant?.id;

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setFaults(await provider.listChecklistFaults(tenantId, { status: filter === "all" ? null : filter }));
      setError(false);
    } catch {
      setError(true);
    }
  }, [provider, tenantId, filter]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <PageHeader title={t("faults.title")} subtitle={t("faults.subtitle")} />
      <div className="filters" role="group" aria-label={t("faults.filterLabel")}>
        {(["open", "resolved", "all"] as const).map((f) => (
          <button key={f} type="button" className={`button button-sm ${filter === f ? "button-primary" : "button-secondary"}`} aria-pressed={filter === f} onClick={() => setFilter(f)}>
            {t(`faults.filter.${f}`)}
          </button>
        ))}
      </div>
      <Panel flush>
        {error ? (
          <EmptyState icon="alert" title={t("common.loadError")} />
        ) : faults === null ? (
          <div className="loading" role="status">
            <span className="spinner" aria-hidden="true" />
            {t("common.loading")}
          </div>
        ) : (
          <FaultsTable data={data} faults={faults} showVehicle onChanged={() => void load()} />
        )}
      </Panel>
      <Note>{t("alarmChecklist.noLock")}</Note>
    </>
  );
}
